import type { MultiComment } from "../zekay/comments";

export interface LeaveType {
  ID: number;
  Name: string;
  Description?: string;
  CompanyID?: number;
  IsActive: boolean;
  RequiresValidation: boolean;
  UsesCounter?: boolean;
  Unit?: "days" | "hours";
  DefaultMonthlyAccrual?: number;
  AllowsNegative?: boolean;
  Color: string;
  CreatedAt?: string;
  UpdatedAt?: string;
}

/** Hourly leave flow when `Unit === "hours"`; otherwise day-based (including missing unit). */
export function isLeaveTypeHourlyUnit(unit: LeaveType["Unit"] | undefined): boolean {
  return unit === "hours";
}

export function leaveTypeUnitLabelFr(unit: LeaveType["Unit"] | undefined): string {
  if (unit === "hours") {
    return "Heures";
  }
  if (unit === "days") {
    return "Jours";
  }
  return "Jours";
}

/** Nested comment row as returned by GET/POST leave APIs (go-zekay-models leave.Comment). */
export interface LeaveRequestComment {
  user: string;
  comment: string;
  date: string;
}

/** GET/POST leave/requests response body (snake_case JSON from API). */
export interface LeaveRequest {
  id: number;
  /** `null` for platform global types (e.g. Souhait d’Indisponibilité). */
  company_id: number | null;
  company_name?: string;
  user_id: number;
  user_name?: string;
  type_id: number;
  type_name?: string;
  type_color?: string;
  planning_slot_label?: string;
  planning_anonymized?: boolean;
  status: number;
  start_date: string;
  end_date: string;
  days: number;
  reason: string | null;
  reason_is_public?: boolean;
  start_at?: string;
  end_at?: string;
  multi_comments?: LeaveRequestComment[] | null;
  reviewed_by: number | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserLeaveTypeBalance {
  ID: number;
  UserID: number;
  CompanyID: number;
  TypeID: number;
  Balance: number;
  LastUpdated: string;
  CreatedAt: string;
  UpdatedAt: string;
  MultiComments?: MultiComment[];
}

export type SetLeaveTypeBalancePayload = {
  Balance?: number;
  MultiComments?: MultiComment[];
};

export interface CreateLeaveRequest {
  TypeID: number;
  StartDate: string;
  EndDate: string;
  StartAt?: string;
  EndAt?: string;
  Reason?: string;
  ReasonIsPublic?: boolean;
  /** Explicit `null`: persist without tenant (global indispo). Omit for normal leave (session tenant). */
  CompanyID?: number | null;
}

export interface UpdateLeaveRequest {
  Status: number;
  MultiComments?: MultiComment[];
  ReviewedBy?: number;
}

export type LeaveRequestSortBy =
  | "start_date_desc"
  | "start_date_asc"
  | "created_at_desc"
  | "created_at_asc";

export interface LeaveRequestsQuery {
  UserID?: number;
  TypeID?: number;
  Status?: number;
  StartDate?: string;
  EndDate?: string;
  /** YYYY-MM-DD; sent as `from` query param when set (preferred over slicing StartDate). */
  From?: string;
  /** YYYY-MM-DD; sent as `to` query param when set (preferred over slicing EndDate). */
  To?: string;
  Page?: number;
  Limit?: number;
  SortBy?: LeaveRequestSortBy;
}

export enum LeaveStatus {
  PENDING = 1,
  APPROVED = 2,
  REJECTED = 3,
  CANCELLED = 4,
  CANCELLATION_REQUESTED = 5,
}

export const LeaveStatusLabels: Record<LeaveStatus, string> = {
  [LeaveStatus.PENDING]: "En attente",
  [LeaveStatus.APPROVED]: "Approuvée",
  [LeaveStatus.REJECTED]: "Refusée",
  [LeaveStatus.CANCELLED]: "Annulée",
  [LeaveStatus.CANCELLATION_REQUESTED]: "Annulation demandée",
};
